import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import HabboImage from '../../../components/HabboImage';
import {
    getAdminStaffTeams,
    createStaffTeam,
    updateStaffTeam,
    deleteStaffTeam,
    getAdminStaffMembers,
    createStaffMember,
    updateStaffMember,
    deleteStaffMember,
} from '../../../services/api';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const BADGE_OPTIONS = [
    { value: '', label: 'No badge' },
    { value: '/img/badge.png', label: 'Founder badge' },
    { value: '/img/badge_s.png', label: 'Silver badge' },
    { value: '/img/badge_b.png', label: 'Bronze badge' },
];

const SOCIAL_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'twitch', label: 'Twitch' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'x', label: 'X' },
    { value: 'instagram', label: 'Instagram' },
];

const EMPTY_MEMBER = {
    teamId: '',
    username: '',
    role: '',
    tint: '#7a2c2c',
    icon: '/img/badge_b.png',
    startMonth: '',
    startYear: '',
    blurb: '',
    socialPlatform: '',
    socialHandle: '',
    sortOrder: 0,
    isActive: true,
};

const EMPTY_TEAM = {
    teamName: '',
    sortOrder: 0,
    isActive: true,
};

const EXACT_PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0,
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded text-sm';
const labelClass = 'block text-sm uppercase mb-1';
const buttonShadow = { boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' };
const smallButtonShadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' };

const errorMessage = (error, fallback) => {
    if (error?.status === 409) return 'Move or remove this team\'s members before deleting it';
    if (error?.status === 400) return 'Please check the details and try again';
    return fallback;
};

function StaffTeamAdmin({ userData }) {
    const [teams, setTeams] = useState([]);
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [teamForm, setTeamForm] = useState(EMPTY_TEAM);
    const [editingTeamId, setEditingTeamId] = useState(null);
    const [showTeamForm, setShowTeamForm] = useState(false);

    const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [showMemberForm, setShowMemberForm] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const [teamsResponse, membersResponse] = await Promise.all([
                getAdminStaffTeams(),
                getAdminStaffMembers(),
            ]);
            if (teamsResponse.success) setTeams(teamsResponse.teams);
            if (membersResponse.success) setMembers(membersResponse.members);
        } catch (error) {
            console.error('Failed to load staff team data:', error);
            toast.error('Failed to load the staff team');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const resetTeamForm = () => {
        setTeamForm(EMPTY_TEAM);
        setEditingTeamId(null);
        setShowTeamForm(false);
    };

    const resetMemberForm = () => {
        setMemberForm(EMPTY_MEMBER);
        setEditingMemberId(null);
        setShowMemberForm(false);
    };

    const handleEditTeam = (team) => {
        setTeamForm({
            teamName: team.teamName,
            sortOrder: team.sortOrder,
            isActive: team.isActive,
        });
        setEditingTeamId(team.teamId);
        setShowTeamForm(true);
    };

    const handleTeamSubmit = async (e) => {
        e.preventDefault();

        if (!teamForm.teamName.trim()) {
            toast.error('Please enter a team name');
            return;
        }

        try {
            if (editingTeamId) {
                await updateStaffTeam(editingTeamId, teamForm);
                toast.success('Team updated');
            } else {
                await createStaffTeam(teamForm);
                toast.success('Team created');
            }
            resetTeamForm();
            fetchAll();
        } catch (error) {
            console.error('Failed to save team:', error);
            toast.error(errorMessage(error, 'Failed to save the team'));
        }
    };

    const handleDeleteTeam = async (team) => {
        if (!window.confirm(`Delete the "${team.teamName}" team?`)) return;

        try {
            await deleteStaffTeam(team.teamId);
            toast.success('Team deleted');
            if (editingTeamId === team.teamId) resetTeamForm();
            fetchAll();
        } catch (error) {
            console.error('Failed to delete team:', error);
            toast.error(errorMessage(error, 'Failed to delete the team'));
        }
    };

    const handleAddMember = (teamId) => {
        setMemberForm({ ...EMPTY_MEMBER, teamId: String(teamId) });
        setEditingMemberId(null);
        setShowMemberForm(true);
    };

    const handleEditMember = (member) => {
        setMemberForm({
            teamId: String(member.teamId),
            username: member.username || '',
            role: member.role || '',
            tint: member.tint || '#7a2c2c',
            icon: member.icon || '',
            startMonth: member.startMonth ? String(member.startMonth) : '',
            startYear: member.startYear ? String(member.startYear) : '',
            blurb: member.blurb || '',
            socialPlatform: member.socialPlatform || '',
            socialHandle: member.socialHandle || '',
            sortOrder: member.sortOrder,
            isActive: member.isActive,
        });
        setEditingMemberId(member.memberId);
        setShowMemberForm(true);
    };

    const handleMemberSubmit = async (e) => {
        e.preventDefault();

        if (!memberForm.teamId) {
            toast.error('Please choose a team');
            return;
        }
        if (!memberForm.username.trim()) {
            toast.error('Please enter a Habbo username');
            return;
        }
        if (!memberForm.role.trim()) {
            toast.error('Please enter a team position');
            return;
        }

        const payload = {
            ...memberForm,
            teamId: Number(memberForm.teamId),
            startMonth: memberForm.startMonth ? Number(memberForm.startMonth) : null,
            startYear: memberForm.startYear ? Number(memberForm.startYear) : null,
            sortOrder: Number(memberForm.sortOrder) || 0,
        };

        try {
            if (editingMemberId) {
                await updateStaffMember(editingMemberId, payload);
                toast.success('Team member updated');
            } else {
                await createStaffMember(payload);
                toast.success('Team member added');
            }
            resetMemberForm();
            fetchAll();
        } catch (error) {
            console.error('Failed to save team member:', error);
            toast.error(errorMessage(error, 'Failed to save the team member'));
        }
    };

    const handleDeleteMember = async (member) => {
        if (!window.confirm(`Remove ${member.username} from the team?`)) return;

        try {
            await deleteStaffMember(member.memberId);
            toast.success('Team member removed');
            if (editingMemberId === member.memberId) resetMemberForm();
            fetchAll();
        } catch (error) {
            console.error('Failed to remove team member:', error);
            toast.error(errorMessage(error, 'Failed to remove the team member'));
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const membersForTeam = (teamId) => members.filter(m => m.teamId === teamId);

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="red" header="Teams">
                <button
                    onClick={() => (showTeamForm ? resetTeamForm() : setShowTeamForm(true))}
                    className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider mb-4"
                    style={buttonShadow}
                >
                    {showTeamForm ? 'Cancel' : 'Add New Team'}
                </button>

                {showTeamForm && (
                    <form onSubmit={handleTeamSubmit} className="bg-gray-100 p-4 rounded mb-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div className="sm:col-span-2">
                                <label className={labelClass}>Team Name</label>
                                <input
                                    type="text"
                                    value={teamForm.teamName}
                                    onChange={(e) => setTeamForm({ ...teamForm, teamName: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Sort Order</label>
                                <input
                                    type="number"
                                    value={teamForm.sortOrder}
                                    onChange={(e) => setTeamForm({ ...teamForm, sortOrder: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <label className="flex items-center cursor-pointer mb-3">
                            <input
                                type="checkbox"
                                checked={teamForm.isActive}
                                onChange={(e) => setTeamForm({ ...teamForm, isActive: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm uppercase">Show on the About page</span>
                        </label>

                        <button
                            type="submit"
                            className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider"
                            style={buttonShadow}
                        >
                            {editingTeamId ? 'Save Team' : 'Create Team'}
                        </button>
                    </form>
                )}

                {isLoading ? (
                    <p className="text-sm uppercase">Loading teams...</p>
                ) : teams.length === 0 ? (
                    <p className="text-sm uppercase">No teams created yet</p>
                ) : (
                    <div className="space-y-2">
                        {teams.map((team) => (
                            <div key={team.teamId} className="bg-gray-100 p-3 rounded flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-base uppercase font-bold">
                                        {team.teamName}
                                        {!team.isActive && <span className="ml-2 text-sm text-gray-500">(hidden)</span>}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        Sort order {team.sortOrder} &middot; {team.memberCount} member{team.memberCount === 1 ? '' : 's'}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleEditTeam(team)}
                                    className="bg-blue-600 text-white px-3 py-1 text-xs uppercase"
                                    style={smallButtonShadow}
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleAddMember(team.teamId)}
                                    className="bg-green-700 text-white px-3 py-1 text-xs uppercase"
                                    style={smallButtonShadow}
                                >
                                    Add Member
                                </button>
                                <button
                                    onClick={() => handleDeleteTeam(team)}
                                    className="bg-red-600 text-white px-3 py-1 text-xs uppercase"
                                    style={smallButtonShadow}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </ContentBox>

            <ContentBox theme="default" tint="red" header="Team Members">
                <button
                    onClick={() => (showMemberForm ? resetMemberForm() : setShowMemberForm(true))}
                    className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider mb-4"
                    style={buttonShadow}
                >
                    {showMemberForm ? 'Cancel' : 'Add New Team Member'}
                </button>

                {showMemberForm && (
                    <form onSubmit={handleMemberSubmit} className="bg-gray-100 p-4 rounded mb-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className={labelClass}>Team</label>
                                <select
                                    value={memberForm.teamId}
                                    onChange={(e) => setMemberForm({ ...memberForm, teamId: e.target.value })}
                                    className={inputClass}
                                >
                                    <option value="">Select a team...</option>
                                    {teams.map((team) => (
                                        <option key={team.teamId} value={team.teamId}>{team.teamName}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Habbo Username</label>
                                <input
                                    type="text"
                                    value={memberForm.username}
                                    onChange={(e) => setMemberForm({ ...memberForm, username: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className={labelClass}>Team Position</label>
                            <input
                                type="text"
                                value={memberForm.role}
                                onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                                placeholder="Team Leader | Furni Values"
                                className={inputClass}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className={labelClass}>Start Month</label>
                                <select
                                    value={memberForm.startMonth}
                                    onChange={(e) => setMemberForm({ ...memberForm, startMonth: e.target.value })}
                                    className={inputClass}
                                >
                                    <option value="">Not set</option>
                                    {MONTHS.map((month, index) => (
                                        <option key={month} value={index + 1}>{month}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Start Year</label>
                                <input
                                    type="number"
                                    min="2000"
                                    max="2100"
                                    value={memberForm.startYear}
                                    onChange={(e) => setMemberForm({ ...memberForm, startYear: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className={labelClass}>Tint Colour</label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={/^#[0-9a-fA-F]{6}$/.test(memberForm.tint) ? memberForm.tint : '#7a2c2c'}
                                        onChange={(e) => setMemberForm({ ...memberForm, tint: e.target.value })}
                                        className="w-12 h-[38px] border border-gray-300 rounded cursor-pointer p-0"
                                    />
                                    <input
                                        type="text"
                                        value={memberForm.tint}
                                        onChange={(e) => setMemberForm({ ...memberForm, tint: e.target.value })}
                                        placeholder="#6ce083"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Header Badge</label>
                                <select
                                    value={memberForm.icon}
                                    onChange={(e) => setMemberForm({ ...memberForm, icon: e.target.value })}
                                    className={inputClass}
                                >
                                    {BADGE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className={labelClass}>Blurb</label>
                            <textarea
                                value={memberForm.blurb}
                                onChange={(e) => setMemberForm({ ...memberForm, blurb: e.target.value })}
                                className={inputClass}
                                rows="4"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div>
                                <label className={labelClass}>Social Platform</label>
                                <select
                                    value={memberForm.socialPlatform}
                                    onChange={(e) => setMemberForm({ ...memberForm, socialPlatform: e.target.value })}
                                    className={inputClass}
                                >
                                    {SOCIAL_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Social Handle</label>
                                <input
                                    type="text"
                                    value={memberForm.socialHandle}
                                    onChange={(e) => setMemberForm({ ...memberForm, socialHandle: e.target.value })}
                                    disabled={!memberForm.socialPlatform}
                                    placeholder="username"
                                    className={`${inputClass} disabled:bg-gray-200`}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Sort Order</label>
                                <input
                                    type="number"
                                    value={memberForm.sortOrder}
                                    onChange={(e) => setMemberForm({ ...memberForm, sortOrder: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <label className="flex items-center cursor-pointer mb-3">
                            <input
                                type="checkbox"
                                checked={memberForm.isActive}
                                onChange={(e) => setMemberForm({ ...memberForm, isActive: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm uppercase">Show on the About page</span>
                        </label>

                        <button
                            type="submit"
                            className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider"
                            style={buttonShadow}
                        >
                            {editingMemberId ? 'Save Team Member' : 'Add Team Member'}
                        </button>
                    </form>
                )}

                {isLoading ? (
                    <p className="text-sm uppercase">Loading team members...</p>
                ) : members.length === 0 ? (
                    <p className="text-sm uppercase">No team members added yet</p>
                ) : (
                    <div className="space-y-4">
                        {teams.map((team) => {
                            const teamMembers = membersForTeam(team.teamId);
                            if (teamMembers.length === 0) return null;

                            return (
                                <div key={team.teamId}>
                                    <h3 className="font-['Jersey_10'] tracking-widest uppercase text-[20px] mb-2">
                                        {team.teamName}
                                    </h3>
                                    <div className="space-y-2">
                                        {teamMembers.map((member) => (
                                            <div key={member.memberId} className="bg-gray-100 p-3 rounded flex items-center gap-3">
                                                <div
                                                    className="w-10 h-10 border border-black overflow-hidden flex items-center justify-center shrink-0"
                                                    style={{ backgroundColor: member.tint || '#d1d5db' }}
                                                >
                                                    <HabboImage
                                                        habbo={member.username}
                                                        hotel="COM"
                                                        headonly
                                                        direction={2}
                                                        head_direction={2}
                                                        size="s"
                                                        style={EXACT_PIXEL_IMAGE}
                                                        alt={member.username}
                                                    />
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-bold">
                                                        {member.username}
                                                        {!member.isActive && <span className="ml-2 text-xs text-gray-500 font-normal">(hidden)</span>}
                                                    </div>
                                                    <div className="text-xs text-gray-600 truncate">
                                                        {member.role}
                                                        {member.startMonth && member.startYear
                                                            ? ` · Joined ${MONTHS[member.startMonth - 1]} ${member.startYear}`
                                                            : ''}
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => handleEditMember(member)}
                                                    className="bg-blue-600 text-white px-3 py-1 text-xs uppercase"
                                                    style={smallButtonShadow}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteMember(member)}
                                                    className="bg-red-600 text-white px-3 py-1 text-xs uppercase"
                                                    style={smallButtonShadow}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </ContentBox>
        </AdminLayout>
    );
}

export default StaffTeamAdmin;
